import {
    BlockStack,
    Box,
    Button,
    Modal,
    SegmentedControl,
    Select,
    Typography,
    type SegmentedOption,
    type SelectOption,
} from '@pv-frontend/ui-components-web';
import type { CrawlRunScope } from './crawl-format';

const SCOPE_OPTIONS: readonly SegmentedOption<CrawlRunScope>[] = [
    { value: 'ALL_SITES', label: 'All sites' },
    { value: 'SINGLE_SITE', label: 'One site' },
];

export interface RunCrawlModalProps {
    open: boolean;
    onClose: () => void;
    scope: CrawlRunScope;
    onScopeChange: (scope: CrawlRunScope) => void;
    /** Site options for the single-site picker (already labelled; non-crawlable ones disabled). */
    siteOptions: ReadonlyArray<SelectOption>;
    siteId: string;
    onSiteChange: (siteId: string) => void;
    onConfirm: () => void;
    submitting: boolean;
    /** Set when a run is already in flight — the trigger is blocked and this explains why. */
    activeRunNotice?: string | null;
    /** Validation hint under the site picker (e.g. "Choose a site"). */
    siteError?: string;
}

/**
 * The "Run crawl" confirmation (pv-frontend#163): choose scope (all sites / one
 * site) and, for a single-site run, the site. Blocked while a run is already
 * active (the mutation is idempotent w.r.t. the single active run, but we stop the
 * operator before they fire it). Presentational — the container owns the state,
 * the mutation and the toast.
 */
export function RunCrawlModal({
    open,
    onClose,
    scope,
    onScopeChange,
    siteOptions,
    siteId,
    onSiteChange,
    onConfirm,
    submitting,
    activeRunNotice,
    siteError,
}: RunCrawlModalProps) {
    const blocked = Boolean(activeRunNotice);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Run a listings crawl"
            testID="run-crawl-modal"
            actions={
                <>
                    <Button variant="tertiary" onPress={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" onPress={onConfirm} isLoading={submitting} disabled={blocked}>
                        Run crawl
                    </Button>
                </>
            }
        >
            <BlockStack gap="400">
                <Typography tone="text-secondary">
                    A manual crawl ignores the nightly schedule and overnight window — it fetches every
                    approved, opted-in site now. The compliance gate still applies.
                </Typography>

                {blocked ? (
                    <Box background="bg-card" borderColor="border-card" borderWidth="0165" borderRadius="300" padding="300">
                        <Typography tone="text-critical" testID="active-run-notice">
                            {activeRunNotice}
                        </Typography>
                    </Box>
                ) : null}

                <BlockStack gap="150">
                    <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                        Scope
                    </Typography>
                    <SegmentedControl
                        accessibilityLabel="Crawl scope"
                        options={SCOPE_OPTIONS}
                        value={scope}
                        onChange={onScopeChange}
                        disabled={submitting || blocked}
                    />
                </BlockStack>

                {scope === 'SINGLE_SITE' ? (
                    <Select
                        label="Site"
                        placeholder="Choose a site…"
                        value={siteId}
                        onChange={onSiteChange}
                        options={siteOptions}
                        disabled={submitting || blocked}
                        error={siteError}
                    />
                ) : null}
            </BlockStack>
        </Modal>
    );
}
